import React, { useEffect, useState } from "react";
import { Brain, Pencil, Trash2, Check, X, ChevronDown, ChevronUp, Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "../api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

function MemoryRow({ m, user, t, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(m.text);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const clean = text.trim();
    if (!clean || clean === m.text) { setEditing(false); setText(m.text); return; }
    setBusy(true);
    try {
      await api.put(`/buddy/memories/${m.id}`, { user_id: user.id, text: clean });
      toast.success(t("buddy_mem_saved"));
      setEditing(false);
      onChanged();
    } catch (e) { toast.error(t("something_wrong")); } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/buddy/memories/${m.id}`, { params: { user_id: user.id } });
      toast.success(t("buddy_mem_deleted"));
      onChanged();
    } catch (e) { toast.error(t("something_wrong")); } finally { setBusy(false); }
  };

  return (
    <div className="flex items-start gap-2 rounded-md border bg-background px-2.5 py-2" data-testid="buddy-memory-row">
      {editing ? (
        <>
          <Input value={text} onChange={(e) => setText(e.target.value)} maxLength={400} autoFocus
            className="h-8 text-sm flex-1" data-testid="buddy-memory-edit-input"
            onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") { setEditing(false); setText(m.text); } }} />
          <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={save} disabled={busy} data-testid="buddy-memory-save"><Check className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => { setEditing(false); setText(m.text); }} data-testid="buddy-memory-cancel"><X className="h-4 w-4" /></Button>
        </>
      ) : (
        <>
          <div className="flex-1 min-w-0 text-sm leading-snug break-words" data-testid="buddy-memory-text">{m.text}</div>
          <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => setEditing(true)} data-testid="buddy-memory-edit"><Pencil className="h-3.5 w-3.5" /></Button>
          <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0 text-destructive" onClick={remove} disabled={busy} data-testid="buddy-memory-delete"><Trash2 className="h-3.5 w-3.5" /></Button>
        </>
      )}
    </div>
  );
}

/** Settings card: everything Buddy learned about this user (buddy_memories) —
 *  read, edit, delete, add. */
export const BuddyMemoriesCard = ({ user, t, defaultOpen = false }) => {
  const [open, setOpen] = useState(!!defaultOpen);
  const [items, setItems] = useState(null);
  const [adding, setAdding] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/buddy/memories", { params: { user_id: user.id } })
      .then((r) => setItems(r.data?.memories || r.data?.items || []))
      .catch(() => setItems([]));
  };
  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    const clean = adding.trim();
    if (!clean) return;
    setBusy(true);
    try {
      await api.post("/buddy/memories", { user_id: user.id, text: clean });
      setAdding("");
      toast.success(t("buddy_mem_saved"));
      load();
    } catch (e) { toast.error(t("something_wrong")); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border p-3.5" data-testid="settings-buddy-memories">
      <button type="button" className="w-full flex items-center gap-3 text-left" onClick={() => setOpen((v) => !v)} data-testid="settings-buddy-memories-toggle">
        <Brain className="h-5 w-5 text-primary shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">{t("buddy_mem_title")}</div>
          <div className="text-xs text-muted-foreground">{t("buddy_mem_hint")}</div>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
      </button>
      {open && (
        <div className="mt-3 space-y-2">
          <div className="flex items-center gap-2">
            <Input value={adding} onChange={(e) => setAdding(e.target.value)} placeholder={t("buddy_mem_add_ph")} maxLength={400}
              className="h-9 text-sm flex-1" data-testid="buddy-memory-add-input"
              onKeyDown={(e) => { if (e.key === "Enter") add(); }} />
            <Button size="icon" className="h-9 w-9 shrink-0" onClick={add} disabled={busy || !adding.trim()} data-testid="buddy-memory-add-btn"><Plus className="h-4 w-4" /></Button>
          </div>
          {items === null && <div className="text-xs text-muted-foreground text-center py-3">…</div>}
          {items !== null && items.length === 0 && (
            <div className="text-xs text-muted-foreground text-center py-3" data-testid="buddy-memory-empty">{t("buddy_mem_empty")}</div>
          )}
          {(items || []).map((m) => <MemoryRow key={m.id} m={m} user={user} t={t} onChanged={load} />)}
        </div>
      )}
    </div>
  );
};
